import { useQuery } from '@tanstack/react-query'
import { Avatar, Botao, Menu, cn } from '@baiocchi/ui'
import { useNavigate } from 'react-router-dom'

import { chaves } from '../api/chaves'
import { notificacoesApi } from '../api/catalogos'
import { useSessao } from '../autenticacao/contexto-sessao'
import {
  IconeLua,
  IconeMenu,
  IconeSair,
  IconeSino,
  IconeSol,
  IconeUsuario,
} from '../componentes/icones'
import { useTema } from '../ganchos/usar-tema'

export interface PropriedadesTopbar {
  aoAlternarSidebar: () => void
  aoAbrirNoCelular: () => void
}

/**
 * Topbar — botão de menu, título do sistema, notificações, tema e perfil.
 *
 * O filtro global de período que o legado punha aqui **não** subiu para a topbar:
 * ele só existia para o dashboard, e uma barra global que muda o significado de
 * uma tela só ensina o usuário a desconfiar dela. O filtro mora na própria
 * TELA-01, na URL, e por isso é compartilhável por link.
 */
export function Topbar({ aoAlternarSidebar, aoAbrirNoCelular }: PropriedadesTopbar) {
  const { usuario, sair } = useSessao()
  const { escuro, alternar } = useTema()
  const navegar = useNavigate()

  /**
   * MVP é polling do contador (§11) — 60 s. O contrato de leitura não muda quando
   * o SSE entrar na Fase 2, então esta consulta some sem mexer na tela.
   *
   * `retry: false` e falha silenciosa (sem badge) continuam de propósito: o contador
   * é enfeite informativo, e um erro no canto da tela a cada minuto não ajuda
   * ninguém. Antes do INT-06 essa tolerância escondia um `404` permanente — a rota
   * não existia. Hoje existe; a tolerância vale para indisponibilidade de verdade.
   */
  const contador = useQuery({
    queryKey: chaves.notificacoes.contador,
    queryFn: ({ signal }) => notificacoesApi.contador(signal),
    refetchInterval: 60_000,
    retry: false,
    staleTime: 30_000,
  })

  const naoLidas = contador.data?.naoLidas ?? 0

  return (
    <header className="sticky top-0 z-30 flex h-topbar shrink-0 items-center gap-2 border-b border-borda bg-superficie px-3 sm:px-4">
      <Botao
        variante="fantasma"
        tamanho="icone"
        onClick={aoAlternarSidebar}
        aria-label="Recolher ou expandir o menu"
        className="hidden lg:inline-flex"
      >
        <IconeMenu />
      </Botao>

      <Botao
        variante="fantasma"
        tamanho="icone"
        onClick={aoAbrirNoCelular}
        aria-label="Abrir o menu"
        className="lg:hidden"
      >
        <IconeMenu />
      </Botao>

      <span className="ml-1 hidden text-sm font-semibold uppercase tracking-wide text-conteudo-suave sm:inline">
        Baiocchi Leads
      </span>

      <div className="flex-1" />

      <Botao
        variante="fantasma"
        tamanho="icone"
        onClick={alternar}
        aria-label={escuro ? 'Usar tema claro' : 'Usar tema escuro'}
        title={escuro ? 'Tema claro' : 'Tema escuro'}
      >
        {escuro ? <IconeSol /> : <IconeLua />}
      </Botao>

      <Botao
        variante="fantasma"
        tamanho="icone"
        onClick={() => navegar('/notificacoes')}
        aria-label={naoLidas > 0 ? `${naoLidas} notificações não lidas` : 'Notificações'}
        className="relative"
      >
        <IconeSino />
        {naoLidas > 0 && (
          <span
            className={cn(
              'absolute right-1 top-1 flex min-w-4 items-center justify-center rounded-full bg-perigo px-1',
              'text-[0.5625rem] font-bold leading-4 text-white',
            )}
          >
            {naoLidas > 99 ? '99+' : naoLidas}
          </span>
        )}
      </Botao>

      <Menu
        gatilho={
          <button
            type="button"
            className="flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-superficie-3"
            aria-label="Abrir menu do usuário"
          >
            <Avatar
              nome={usuario?.nome ?? '—'}
              fotoUrl={usuario?.fotoUrl ?? null}
              tamanho="sm"
            />
            <span className="hidden max-w-32 truncate text-sm font-medium text-conteudo sm:inline">
              {usuario?.nome ?? '—'}
            </span>
          </button>
        }
        itens={[
          {
            rotulo: 'Meu perfil',
            icone: <IconeUsuario className="h-4 w-4" />,
            // react-router 7 devolve `Promise` de `navigate`. `void` e não `await`:
            // navegar é o efeito, não há o que fazer depois nem erro a tratar.
            onSelecionar: () => void navegar('/perfil'),
          },
          {
            rotulo: 'Sair',
            icone: <IconeSair className="h-4 w-4" />,
            destrutivo: true,
            onSelecionar: () => {
              void sair().then(() => navegar('/entrar', { replace: true }))
            },
          },
        ]}
      />
    </header>
  )
}
